/**
 * SN_SAI_CON_REG_001 — AI 센터 연결 (거래처 DB · 본사 담당자가 넣는다) (2026-09-18, 2차 검토 확정)
 *
 * 중앙 AI 서버 접속 정보(sai_config) — 회사별 한 행이라 프리폼 한 장이다. 서버주소 · 거래처코드 · 접속ID · 비밀번호 · 사용여부 · 비밀번호 설정(읽기) · 최종확인일시 · 확인결과.
 * 비밀번호 칸은 SnPasswordBox(별표). 저장 후 다시 보이지 않고 "비밀번호 설정" 만 켜진다. 바꿀 때만 다시 넣는다.
 * [접속테스트] 는 중앙에서 토큰을 한 번 받아 본다. 저장한 값은 서버 재시작 없이 다음 질문부터 쓰인다.
 * 거래처 그룹에는 이 메뉴 권한을 주지 않는다 — 본사 담당자가 거래처 SNOTES 에 로그인해 넣는다. 환경(테스트 · 운영) 칸은 없다.
 *
 * 폼 정의는 DB(sys_formctrl freeform1, 08_AI_화면설정.sql). 조회는 sys_query sn_sai_con_reg_001_01.
 * 본 : 암호보안정책 SN_MAS_SET_REG_010 (프리폼 단건)
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import SnForm from "@widgets/SnForm";
import SnButton from "@shared/components/SnButton";
import { showDialog } from "@widgets/CommonDialog";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_CON_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const formRef = useRef<any>(null);

  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>();
  const [initFlag, setInitFlag] = useState<boolean>(false);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.form?.freeform1 || []);
        setInitFlag(true);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (initFlag) {
      onSearch();
      setInitFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFlag]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  const onSearch = async () => {
    try {
      const modified = formRef.current?.getChange?.();
      if (modified && modified.modifiedRowcount > 0) {
        const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
        if (confirm) {
          const isSaved: any = await onSave();
          if (!isSaved) return;
        } else if (confirm === undefined) {
          return;
        }
      }
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남아
         접속테스트가 막힌다 (2026-09-22) */
      formRef.current?.resetUpdate?.();
      const data: any = await fnGetDataSet({ id: "sn_sai_con_reg_001_01", params: {} });
      const row = data?.[0]?.[0];
      if (row) {
        setFormData(row);
      } else {
        /* 아직 한 행도 없다 — 빈 행을 새로 만든다. 저장하면 insert 로 간다 */
        setFormData(undefined);
        formRef.current?.addRow?.({ cd_firm: userInfo.cd_firm, yn_use: "Y", yn_secret: "N" });
      }
    } catch (error) {}
  };

  const onSave = async () => {
    try {
      const change = formRef.current.getChange();
      if (!change || change.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const isSaved = await fnSave("SN_SAI_CON_REG_001_SAVE_01", { sai_config: change });
      if (!isSaved) return false;
      formRef.current.resetUpdate();
      await onSearch();
      return true;
    } catch (error) {
      formRef.current?.resetUpdate?.(true);
      return false;
    }
  };

  /** [접속테스트] — 저장된 값으로 중앙 토큰을 한 번 받아 본다 */
  const onCheck = async () => {
    const change = formRef.current?.getChange?.();
    if (change && change.modifiedRowcount > 0) {
      await showDialog("alter", "수정 중인 내용을 먼저 저장해 주세요.");
      return;
    }
    /* 결과 문구는 callApi 가 띄운다. 여기서 또 띄우면 같은 말이 두 번 나온다 */
    const result = await callApi("SN_SAI_CON_REG_001_btnCheck", {});
    if (!result?.status) return;
    await onSearch();
  };

  return (
    <div className="h-full flex flex-col">
      <SnForm
        ref={formRef}
        data={formData}
        controls={formControlData}
        etcProps={{ title: "접속 정보", labelSize: 140 }}
        header={
          <div className="flex justify-end">
            <SnButton text={"접속테스트"} onClick={() => { onCheck(); }} />
          </div>
        }
      />
    </div>
  );
});

export default SN_SAI_CON_REG_001;
